import api from '../helpers/axios';

// ==================== TYPES ====================

export interface Category {
    categoryId: number;
    categoryName: string;
    categoryCode: string;
    categoryDescription: string;
    categoryIcon: string;
    categoryColor: string;
    displayOrder: number;
    isActive: boolean;
    isFeatured: boolean;
    articleCount: number;
    parentCategoryId?: number;
    parentCategoryName?: string;
    children?: Category[] | null;   // backward-compat alias used by CategorySelector / useCategories
    createdBy?: string;
    updatedBy?: string;
}

// Used only for hierarchy endpoint responses which nest sub-categories
export interface CategoryTreeNode extends Category {
    subCategories: CategoryTreeNode[];
}

export interface CategoryCreatePayload {
    categoryName: string;
    categoryCode: string;
    categoryDescription?: string;
    categoryIcon?: string;
    categoryColor?: string;
    displayOrder?: number;
    isActive: boolean;
    isFeatured?: boolean;
    parentCategoryId?: number | null;
}

export interface CategoryAssignmentPayload {
    articleId: number;
    categoryIds: number[];
    primaryCategoryId: number;
}

export interface ArticleCategoryResponse {
    articleId: number;
    categories: Category[];
    primaryCategory: Category;
}

export interface CategoryPage {
    content: Category[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
}

export interface CategoryPageParams {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    search?: string;
    active?: boolean;
    featured?: boolean;
    parentId?: number;
}

// ==================== SERVICE ====================

class CategoryService {

    // ---- Listing & Read ----

    async getActiveCategories(): Promise<Category[]> {
        const response = await api.get('/api/categories/active');
        return response.data;
    }

    async getAllCategories(): Promise<Category[]> {
        const response = await api.get('/api/categories/all');
        return response.data;
    }

    async getRootCategories(): Promise<Category[]> {
        const response = await api.get('/api/categories/root');
        return response.data;
    }

    async getFeaturedCategories(): Promise<Category[]> {
        const response = await api.get('/api/categories/featured');
        return response.data;
    }

    async getCategoryById(id: number): Promise<Category> {
        const response = await api.get(`/api/categories/${id}`);
        return response.data;
    }

    async getCategoryByCode(code: string): Promise<Category> {
        const response = await api.get(`/api/categories/code/${code}`);
        return response.data;
    }

    async getSubCategories(parentId: number): Promise<Category[]> {
        const response = await api.get(`/api/categories/${parentId}/subcategories`);
        return response.data;
    }

    async getCategoryHierarchy(signal?: AbortSignal): Promise<CategoryTreeNode[]> {
        const response = await api.get('/api/categories/hierarchy', { signal });
        return response.data;
    }

    async getCategoryPath(id: number): Promise<Category[]> {
        const response = await api.get(`/api/categories/${id}/path`);
        return response.data;
    }

    async getCategoriesWithPagination(params: CategoryPageParams): Promise<CategoryPage> {
        const response = await api.get('/api/categories', { params });
        return response.data;
    }

    // ---- Statistics ----

    async getCategoryStatistics(): Promise<Record<number, number>> {
        const response = await api.get('/api/categories/statistics');
        return response.data;
    }

    async getCategoryArticleCount(id: number): Promise<{ categoryId: number; articleCount: number }> {
        const response = await api.get(`/api/categories/${id}/count`);
        return response.data;
    }

    // ---- CRUD ----

    async createCategory(payload: CategoryCreatePayload): Promise<Category> {
        const response = await api.post('/api/categories', payload);
        return response.data;
    }

    async updateCategory(id: number, payload: CategoryCreatePayload): Promise<Category> {
        const response = await api.put(`/api/categories/${id}`, payload);
        return response.data;
    }

    async deleteCategory(id: number): Promise<void> {
        await api.delete(`/api/categories/${id}`);
    }

    // ---- Article-Category assignment ----

    async assignCategoriesToArticle(payload: CategoryAssignmentPayload): Promise<void> {
        await api.post('/api/categories/assign', payload);
    }

    async getArticleCategories(articleId: number): Promise<ArticleCategoryResponse> {
        const response = await api.get(`/api/categories/article/${articleId}`);
        return response.data;
    }

    async getArticlePrimaryCategory(articleId: number): Promise<Category> {
        const response = await api.get(`/api/categories/article/${articleId}/primary`);
        return response.data;
    }

    async removeCategoryFromArticle(categoryId: number, articleId: number): Promise<void> {
        await api.delete(`/api/categories/${categoryId}/article/${articleId}`);
    }

    async updatePrimaryCategory(articleId: number, categoryId: number): Promise<void> {
        await api.put(`/api/categories/article/${articleId}/primary/${categoryId}`);
    }
}

export const categoryService = new CategoryService();
export default categoryService;
